import { FormControl } from '@angular/forms';

import { CatsRecordTypeEnum, CatsStatusCodeEnum } from '@shared-enums/cats.enum';
import { arrayIsTruthy } from '@shared-helpers/array-object-helpers';
import { limitDecimalDisplay, safeCompare, trimDecimalZeros } from '@shared-helpers/string-number-helpers';
import { IForemanCatsProperties, IForemanEquipment, IHttpResponseEquipment } from '@shared-interfaces/api';

import { ICreateEquipment } from '../models/cats-outbound.model';
import { IModifyCatsRecordHours } from '../models/cats-outbound.model';
import { DatahubService } from '../services/datahub.service';

export class Equipment {
  display: string; //value that will show in the application.
  value = new FormControl<string>('', { nonNullable: true }); //actual sap value in reactive form control
  weDateTotal = 0; //tracks this objects total across all timesheets for the week
  workDateTotal = 0; //tracks this objects total across all timesheets for the day
  listener: any;
  catsRecords: Array<IForemanCatsProperties> = []; //Cats record this item relates to.
  shouldUpload = false;
  previousValue = 0; //use this to update the weekly total without having to recalc everything. stores value before change event.
  //fields straight from SAP
  EquipmentId: string;
  Description: string;
  Type: string;
  CostCenter: string;
  ServiceLine: string;
  InvalidDates: string;
  canDelete = false;
  CCWeek: string;

  constructor(
    sapRecord: IForemanEquipment,
    private readonly datahub: DatahubService,
  ) {
    this.catsRecords = sapRecord.records;
    this.EquipmentId = sapRecord.EquipmentId;
    this.Description = sapRecord.Description;
    this.Type = sapRecord.Type;
    this.CostCenter = sapRecord.CostCenter;
    this.ServiceLine = sapRecord.ServiceLine;
    this.InvalidDates = sapRecord.InvalidDates;
    this.CCWeek = sapRecord.CCWeek;
    this.display = sapRecord.display;
    this.weDateTotal = Number(sapRecord.weeklyTotal);
    this.workDateTotal = Number(sapRecord.dailyTotal);
    this.resolveArrayToRow(this.catsRecords);
  }

  findTotals(weeklyRecords: IForemanCatsProperties[]): number {
    let sum = 0;
    if (arrayIsTruthy(weeklyRecords)) {
      weeklyRecords.forEach((x) => {
        const date = this.datahub.date.convertSapDateToMoment(x.Workdate);
        const hours = Number(x.Catshours);
        if (safeCompare(x.Zequipment, this.EquipmentId)) sum += hours;
        if (this.datahub.date.isSameDay(this.datahub.date.getMoment(), date)) this.workDateTotal += hours;
      });
    }
    this.weDateTotal = sum;
    return sum;
  }

  makeEquipmentCatRecord(): ICreateEquipment {
    return {
      ZempCreatedBy: this.datahub.timesheet.findTokenEmployee(),
      Extdocumentno: this.datahub.extDocument.Documentno,
      Zmatnr: this.datahub.contract.getEquipmentDefault(this),
      Employeenumber: this.datahub.timesheet.findTokenEmployee(),
      Catshours: this.value.value.trim() === '' ? '0' : this.value.value.toString(),
      Workdate: this.datahub.date.getOdataString(),
      Acttype: this.Type,
      RecOrder: this.datahub.contract.getServiceOrder(),
      Zequipment: this.EquipmentId,
      Zrecordtype: CatsRecordTypeEnum.Equipment,
      SendCctr: this.CostCenter,
    };
  }

  public getCatRecords(): IModifyCatsRecordHours | ICreateEquipment | null {
    if (this.shouldUpload) {
      if (this.catsRecords.length > 0) {
        if (this.catsRecords[0] === undefined) throw Error('tried to modify a record that did not exist!');
        return this.formatModify(this.catsRecords[0]);
      } else return this.makeEquipmentCatRecord();
    } else return null;
  }

  resolveArrayToRow(catsArray: any[]): void {
    if (!arrayIsTruthy(catsArray)) {
      this.setControlValueAndListen('', false);
      return;
    } //if there are no sap records, initialize an empty form.
    const isApproved = !!catsArray.find((x) => Number(x.Status) > CatsStatusCodeEnum.ReadyForApproval);
    if (catsArray.length === 1) {
      const hours = Number(catsArray[0].Catshours ?? '');
      this.setControlValueAndListen(hours.toString(), isApproved, isApproved ? 'alreadyApprovedError' : '');
      return;
    } //don't permit changes to approved records.
    const total = catsArray.reduce((x, y) => Number(x.Catshours) + Number(y.Catshours));
    this.setControlValueAndListen(total, true, 'multipleRecordsForDayError'); //don't permit changes to complex types
  }

  private setControlValueAndListen(value: string | number, isDisabled: boolean, error?: string): void {
    this.value = new FormControl<string>({ value: value.toString(), disabled: isDisabled }, { nonNullable: true });
    if (error) {
      const errorObj: Record<string, boolean> = {}; //errors have to be objects for reactive controls
      errorObj[error] = true;
      this.value.setErrors(errorObj);
    }
    this.previousValue = Number(this.value.value);
    this.listener = this.value.valueChanges.subscribe((valChange) => {
      const tempNum = Number(valChange); //transform the value into a number if null
      if (arrayIsTruthy(this.catsRecords)) {
        if (this.catsRecords.length === 1 && this.catsRecords[0]) this.catsRecords[0].Catshours = tempNum.toString();
      }
      this.updateTotals(tempNum); //when the form changes, we can update the cats record in memory. For equipment, the field is Catshours. Then flag the record for upload.
      this.shouldUpload = true;
    });
  }

  private updateTotals(value: number): void {
    //calc the difference in the values to update the weekly total.
    const difference = value - this.previousValue;
    this.weDateTotal += difference;
    this.workDateTotal += difference;
    this.previousValue = value;
  }

  findCounters(): string[] {
    const counters: string[] = [];
    this.catsRecords.forEach((record) => {
      const counter = record.Counter;
      if (counter) counters.push(counter);
    });
    return counters;
  }

  setValue(value: string): void {
    this.value.setValue(value);
  }

  removeSelf(equipmentArray: any[], shouldUnsubscribe?: boolean): void {
    const index = equipmentArray.findIndex((equipment) => equipment.EquipmentId === this.EquipmentId);
    if (index < 0) return;
    equipmentArray.splice(index, 1);
    if (shouldUnsubscribe) this.listener.unsubscribe();
  }

  deleteSelf(equipmentArray: any[]): void {
    this.datahub.timesheet.flagForDelete(this);
    this.removeSelf(equipmentArray);
  }

  addSelf(equipmentArray: any[]): void {
    equipmentArray.push(this);
    this.datahub.timesheet.saveCrew();
  }

  getGraphableValues() {
    return [
      this.display,
      this.value.value,
      '',
      '',
      trimDecimalZeros(limitDecimalDisplay(this.workDateTotal)),
      trimDecimalZeros(limitDecimalDisplay(this.weDateTotal)),
    ];
  }

  getSapObject(): IHttpResponseEquipment {
    return {
      EquipmentId: this.EquipmentId,
      Description: this.Description,
      Type: this.Type,
      CostCenter: this.CostCenter,
      ServiceLine: this.ServiceLine,
      CCWeek: this.CCWeek,
      InvalidDates: this.InvalidDates,
    };
  }

  private formatModify(catsRecord: IForemanCatsProperties): IModifyCatsRecordHours {
    return {
      Counter: catsRecord.Counter,
      Catshours: this.value.value.trim() === '' ? '0' : this.value.value.toString(),
      Catshours_X: true,
    };
  }
}
